import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import PermissionRequiredBanner from '@/components/PermissionRequiredBanner';
import Icon from '@/components/Icon';
import {
  commitReview,
  dismissReview,
  getReviewItem,
  listOpenOrders,
  categorize,
  categoryLabel,
  categoryTone,
  type CommitResult,
  type OpenOrderRow,
  type ParsedLineItem,
  type ParsedPayload,
  type ReviewQueueDetail,
} from '@/lib/reviewQueue';

// ============================================================
// ReviewDetailPage — Phase 3, Epic 3A.6
// ============================================================
// Stacked editor for a single inbound_emails row in the review
// queue.
//
// Layout (top-to-bottom):
//   1. Header bar — back link, dismiss button, commit button
//   2. Status summary — category chip, dispatch_status, last
//      dispatched_at
//   3. Editable payload — vendor / email_type / order_references
//      / line items
//   4. Order picker — when category is 'needs_match', a search-
//      and-pick dropdown of open orders
//   5. Read-only payload extras — total, ordered_at, shipping
//   6. Raw email body — collapsed by default; toggle expands
//   7. Attachment metadata — collapsed by default
//
// Edit precedence: detail loads `corrected_payload ?? parsed_payload`
// as the initial edit state. Saving writes corrected_payload (full
// overwrite of the editable shape), then invokes the matcher with
// force=true.
//
// Editable fields (locked at 3A.6 Sub-Q1 = Option A):
//   - email_type (dropdown)
//   - order_references[] (text inputs, add/remove)
//   - vendor (free text)
//   - line_items[].qty
//   - line_items[].gtin / vendor_sku / customer_sku
//
// Read-only fields:
//   - total, ordered_at, shipping (display only)
//   - line_items[].name / uom / unit_price
// These don't affect matcher outcomes; promotable later if pilot
// signal demands.
// ============================================================

const EMAIL_TYPE_OPTIONS = [
  'order_confirmation',
  'shipping_confirmation',
  'invoice',
  'credit_memo',
  'bill_only',
  'other',
] as const;
type EmailTypeOption = (typeof EMAIL_TYPE_OPTIONS)[number];

interface LineItemEdit {
  name: string;
  qty: number | '';
  unit_price: number | '';
  uom: string;
  gtin: string;
  vendor_sku: string;
  customer_sku: string;
}

interface EditState {
  vendor: string;
  email_type: EmailTypeOption | '';
  order_references: string[];
  line_items: LineItemEdit[];
}

function asEmailType(v: unknown): EmailTypeOption | '' {
  return typeof v === 'string' &&
    (EMAIL_TYPE_OPTIONS as readonly string[]).includes(v)
    ? (v as EmailTypeOption)
    : '';
}

function leafString(leaf: unknown): string {
  if (typeof leaf !== 'object' || leaf === null) return '';
  const v = (leaf as { value?: unknown }).value;
  return typeof v === 'string' ? v : '';
}

function leafNumber(leaf: unknown): number | '' {
  if (typeof leaf !== 'object' || leaf === null) return '';
  const v = (leaf as { value?: unknown }).value;
  return typeof v === 'number' ? v : '';
}

function buildInitialEdit(payload: ParsedPayload | null): EditState {
  if (!payload) {
    return {
      vendor: '',
      email_type: '',
      order_references: [],
      line_items: [],
    };
  }
  const lineItems: ParsedLineItem[] = Array.isArray(payload.line_items)
    ? payload.line_items
    : [];
  return {
    vendor: leafString(payload.vendor),
    email_type: asEmailType(
      typeof payload.email_type === 'object' && payload.email_type !== null
        ? (payload.email_type as { value?: unknown }).value
        : payload.email_type,
    ),
    order_references: Array.isArray(payload.order_references)
      ? payload.order_references.filter(
          (s): s is string => typeof s === 'string',
        )
      : [],
    line_items: lineItems.map((li) => ({
      name: leafString(li.name),
      qty: leafNumber(li.qty),
      unit_price: leafNumber(li.unit_price),
      uom: leafString(li.uom),
      gtin: leafString(li.gtin),
      vendor_sku: leafString(li.vendor_sku),
      customer_sku: leafString(li.customer_sku),
    })),
  };
}

/**
 * Build a corrected_payload from the edit state. Wraps each
 * editable field in a {value, confidence: 1} leaf — human edits
 * are recorded with full confidence. Read-only fields (total,
 * ordered_at, shipping, line name/uom/unit_price) are pulled
 * from the source payload so the matcher sees a complete shape.
 */
function buildCorrectedPayload(
  edit: EditState,
  source: ParsedPayload | null,
): ParsedPayload {
  const out: ParsedPayload = {
    ...(source ?? {}),
    vendor: { value: edit.vendor.trim(), confidence: 1 },
    order_references: edit.order_references
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
    line_items: edit.line_items.map((li, idx) => {
      const sourceLi = source?.line_items?.[idx];
      const merged: ParsedLineItem = { ...(sourceLi ?? {}) };
      if (li.qty !== '') {
        merged.qty = { value: li.qty, confidence: 1 };
      }
      if (li.gtin.trim()) {
        merged.gtin = { value: li.gtin.trim(), confidence: 1 };
      }
      if (li.vendor_sku.trim()) {
        merged.vendor_sku = { value: li.vendor_sku.trim(), confidence: 1 };
      }
      if (li.customer_sku.trim()) {
        merged.customer_sku = {
          value: li.customer_sku.trim(),
          confidence: 1,
        };
      }
      return merged;
    }),
  };
  if (edit.email_type) {
    out.email_type = { value: edit.email_type, confidence: 1 };
  }
  return out;
}

export default function ReviewDetailPage(){
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;
  const canView = hasPermission(session, 'inbound_email.review_queue.view');
  const canEdit = hasPermission(session, 'inbound_email.review_queue.edit');
  const canCommit = hasPermission(
    session,
    'inbound_email.review_queue.commit',
  );
  const canDismiss = hasPermission(
    session,
    'inbound_email.review_queue.dismiss',
  );

  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [row, setRow] = useState<ReviewQueueDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opResult, setOpResult] = useState<
    | { kind: 'success'; result: CommitResult }
    | { kind: 'error'; message: string }
    | null
  >(null);

  useEffect(() => {
    if (!canView || !id) return;
    let cancelled = false;
    setLoadError(null);
    setRow(null);
    getReviewItem(clinicId, id)
      .then((r) => {
        if (cancelled) return;
        setRow(r);
        if (r) {
          setEdit(buildInitialEdit(r.corrected_payload ?? r.parsed_payload));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : 'Failed to load review item',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [canView, clinicId, id]);

  const sourcePayload = useMemo(
    () => (row ? row.corrected_payload ?? row.parsed_payload : null),
    [row],
  );

  const onCommit = useCallback(async () => {
    if (!row || !edit) return;
    setBusy(true);
    setOpResult(null);
    try {
      const corrected = buildCorrectedPayload(edit, sourcePayload);
      const result = await commitReview(clinicId, row.id, corrected);
      setOpResult({ kind: 'success', result });
      // Re-fetch the row so the editor reflects the new dispatch
      // outcome without forcing a navigation.
      const fresh = await getReviewItem(clinicId, row.id);
      if (fresh) {
        setRow(fresh);
        setEdit(
          buildInitialEdit(fresh.corrected_payload ?? fresh.parsed_payload),
        );
      }
    } catch (e) {
      setOpResult({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Commit failed',
      });
    } finally {
      setBusy(false);
    }
  }, [row, edit, sourcePayload, clinicId]);

  const onDismiss = useCallback(async () => {
    if (!row) return;
    setBusy(true);
    setOpResult(null);
    try {
      await dismissReview(clinicId, row.id);
      navigate('/review');
    } catch (e) {
      setOpResult({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Dismiss failed',
      });
      setBusy(false);
    }
  }, [row, clinicId, navigate]);

  if (!canView) {
    return (
      <PermissionRequiredBanner
        permission="inbound_email.review_queue.view"
        message="The review queue is available to managers and owners."
      />
    );
  }

  if (loadError) {
    return (
      <div className="p-6 text-center text-sm text-red-700">
        <div className="font-medium">Couldn't load review item</div>
        <div className="text-xs mt-1 font-mono break-words">{loadError}</div>
        <Link
          to="/review"
          className="inline-block mt-3 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold"
        >
          Back to queue
        </Link>
      </div>
    );
  }

  if (!row || !edit) {
    return (
      <div className="h-full flex items-center justify-center">
        <span
          className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  const category = categorize(row);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Header bar */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2">
        <Link
          to="/review"
          aria-label="Back to queue"
          className="w-8 h-8 rounded-lg hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center"
        >
          <Icon
            name="chevright"
            className="w-4 h-4 text-slate-600 rotate-180"
            strokeWidth={2.5}
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">
            {row.subject ?? '(no subject)'}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            From {row.from_name ?? row.from_address}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {canDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-[12px] font-medium text-slate-700 disabled:opacity-50"
            >
              Dismiss
            </button>
          )}
          {canCommit && (
            <button
              type="button"
              onClick={onCommit}
              disabled={busy || !canEdit}
              className="px-2.5 py-1.5 rounded-lg bg-action hover:bg-action-hover active:bg-action-active text-[12px] font-semibold text-white disabled:bg-slate-300"
            >
              {busy ? 'Working…' : 'Commit'}
            </button>
          )}
        </div>
      </div>

      {/* Status summary */}
      <div className="px-3 py-2 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2 text-[11.5px] text-slate-600">
          {category && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset ${categoryTone(category).bg} ${categoryTone(category).text} ${categoryTone(category).ring}`}
            >
              {categoryLabel(category)}
            </span>
          )}
          <span className="font-mono text-slate-500">
            {row.dispatch_status}
          </span>
          {row.dispatched_at && (
            <span className="text-slate-400">
              · last dispatch {new Date(row.dispatched_at).toLocaleString()}
            </span>
          )}
        </div>
        {row.parse_error && (
          <div className="mt-1 text-[11px] text-red-700 font-mono">
            parse_error: {row.parse_error}
          </div>
        )}
      </div>

      {/* Op result banner */}
      {opResult && (
        <OpResultBanner
          result={opResult}
          onDismiss={() => setOpResult(null)}
        />
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Editable section */}
        <section className="bg-white border-b border-slate-200 p-3 space-y-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Parsed (editable)
          </h2>

          <FieldRow label="Vendor">
            <input
              type="text"
              value={edit.vendor}
              onChange={(e) =>
                setEdit({ ...edit, vendor: e.target.value })
              }
              disabled={!canEdit}
              className="w-full text-[13px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white"
            />
          </FieldRow>

          <FieldRow label="Email type">
            <select
              value={edit.email_type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setEdit({
                  ...edit,
                  email_type: asEmailType(e.target.value),
                })
              }
              disabled={!canEdit}
              className="w-full text-[13px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white"
            >
              <option value="">(unset)</option>
              {EMAIL_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </FieldRow>

          <OrderReferencesEditor
            value={edit.order_references}
            onChange={(refs) =>
              setEdit({ ...edit, order_references: refs })
            }
            disabled={!canEdit}
          />

          {category === 'needs_match' && canEdit && (
            <OrderPicker
              clinicId={clinicId}
              vendorFilter={edit.vendor || null}
              currentRefs={edit.order_references}
              onPick={(orderId) => {
                if (edit.order_references.includes(orderId)) return;
                setEdit({
                  ...edit,
                  order_references: [...edit.order_references, orderId],
                });
              }}
            />
          )}

          <LineItemsEditor
            value={edit.line_items}
            onChange={(items) =>
              setEdit({ ...edit, line_items: items })
            }
            disabled={!canEdit}
          />
        </section>

        {/* Read-only summary */}
        <section className="bg-white border-b border-slate-200 p-3 space-y-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Read-only summary
          </h2>
          <ReadOnlySummary payload={sourcePayload} />
        </section>

        {/* Raw email collapsible */}
        <section className="bg-white border-b border-slate-200">
          <button
            type="button"
            onClick={() => setRawOpen((v) => !v)}
            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Raw email
            </span>
            <Icon
              name="chevdown"
              className={`w-4 h-4 text-slate-400 transition-transform ${rawOpen ? 'rotate-180' : ''}`}
              strokeWidth={2}
            />
          </button>
          {rawOpen && (
            <div className="px-3 pb-3">
              {row.raw_text ? (
                <pre className="text-[11px] bg-slate-50 p-2 rounded-lg whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                  {row.raw_text}
                </pre>
              ) : row.raw_html ? (
                <details>
                  <summary className="text-[11px] text-slate-500 cursor-pointer">
                    Raw HTML (no plain-text alternative)
                  </summary>
                  <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded-lg whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                    {row.raw_html}
                  </pre>
                </details>
              ) : (
                <p className="text-[11px] text-slate-400 italic">
                  No body content.
                </p>
              )}
            </div>
          )}
        </section>

        {/* Attachments collapsible */}
        {Array.isArray(row.attachments_json) &&
          row.attachments_json.length > 0 && (
            <section className="bg-white border-b border-slate-200">
              <button
                type="button"
                onClick={() => setAttachOpen((v) => !v)}
                className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Attachments ({row.attachments_json.length})
                </span>
                <Icon
                  name="chevdown"
                  className={`w-4 h-4 text-slate-400 transition-transform ${attachOpen ? 'rotate-180' : ''}`}
                  strokeWidth={2}
                />
              </button>
              {attachOpen && (
                <ul className="px-3 pb-3 space-y-1">
                  {(row.attachments_json as Array<{
                    filename?: string;
                    content_type?: string;
                    size_bytes?: number;
                  }>).map((a, i) => (
                    <li
                      key={i}
                      className="text-[11px] text-slate-600 font-mono"
                    >
                      {a.filename ?? '(unnamed)'}{' '}
                      <span className="text-slate-400">
                        {a.content_type ?? '?'} ·{' '}
                        {a.size_bytes ? `${a.size_bytes} B` : '?'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
      </div>
    </div>
  );
}

// ---- Sub-components ------------------------------------------------

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}){
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-600 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function OrderReferencesEditor({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}){
  return (
    <div>
      <div className="text-[11px] font-medium text-slate-600 mb-1">
        Order references
      </div>
      <div className="space-y-1.5">
        {value.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">
            None.
          </div>
        )}
        {value.map((ref, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              type="text"
              value={ref}
              onChange={(e) => {
                const next = [...value];
                next[i] = e.target.value;
                onChange(next);
              }}
              disabled={disabled}
              className="flex-1 text-[13px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white font-mono"
            />
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              disabled={disabled}
              aria-label="Remove reference"
              className="w-8 h-8 rounded-lg hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center disabled:opacity-50"
            >
              <Icon name="x" className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.5} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...value, ''])}
          disabled={disabled}
          className="text-[11.5px] font-medium text-action hover:text-action-hover disabled:opacity-50"
        >
          + Add reference
        </button>
      </div>
    </div>
  );
}

function LineItemsEditor({
  value,
  onChange,
  disabled,
}: {
  value: LineItemEdit[];
  onChange: (next: LineItemEdit[]) => void;
  disabled: boolean;
}){
  const update = (idx: number, patch: Partial<LineItemEdit>): void => {
    const next = value.map((li, i) => (i === idx ? { ...li, ...patch } : li));
    onChange(next);
  };

  if (value.length === 0) {
    return (
      <div>
        <div className="text-[11px] font-medium text-slate-600 mb-1">
          Line items
        </div>
        <div className="text-[11px] text-slate-400 italic">None parsed.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-[11px] font-medium text-slate-600 mb-1">
        Line items ({value.length})
      </div>
      <ul className="space-y-2">
        {value.map((li, i) => (
          <li
            key={i}
            className="rounded-lg border border-slate-200 p-2 bg-slate-50/50"
          >
            <div className="text-[12px] font-medium text-slate-900 truncate">
              {li.name || '(unnamed)'}
            </div>
            <div className="text-[10.5px] text-slate-500 mb-1.5">
              {li.uom && `${li.uom}`}
              {li.uom && li.unit_price !== '' && ' · '}
              {li.unit_price !== '' && `$${li.unit_price}`}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <label className="block">
                <span className="block text-[10px] text-slate-500">Qty</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={li.qty}
                  onChange={(e) =>
                    update(i, {
                      qty: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                  disabled={disabled}
                  className="w-full text-[12px] px-2 py-1 rounded border border-slate-200 bg-white"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-slate-500">GTIN</span>
                <input
                  type="text"
                  value={li.gtin}
                  onChange={(e) => update(i, { gtin: e.target.value })}
                  disabled={disabled}
                  className="w-full text-[12px] px-2 py-1 rounded border border-slate-200 bg-white font-mono"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-slate-500">
                  Vendor SKU
                </span>
                <input
                  type="text"
                  value={li.vendor_sku}
                  onChange={(e) => update(i, { vendor_sku: e.target.value })}
                  disabled={disabled}
                  className="w-full text-[12px] px-2 py-1 rounded border border-slate-200 bg-white font-mono"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-slate-500">
                  Customer SKU
                </span>
                <input
                  type="text"
                  value={li.customer_sku}
                  onChange={(e) => update(i, { customer_sku: e.target.value })}
                  disabled={disabled}
                  className="w-full text-[12px] px-2 py-1 rounded border border-slate-200 bg-white font-mono"
                />
              </label>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrderPicker({
  clinicId,
  vendorFilter,
  currentRefs,
  onPick,
}: {
  clinicId: string;
  vendorFilter: string | null;
  currentRefs: string[];
  onPick: (orderId: string) => void;
}){
  const [orders, setOrders] = useState<OpenOrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setOrders(null);
    listOpenOrders(clinicId, vendorFilter)
      .then((r) => {
        if (cancelled) return;
        setOrders(r);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load orders');
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, vendorFilter]);

  const filtered = useMemo(() => {
    if (!orders) return null;
    if (!search.trim()) return orders;
    const q = search.trim().toLowerCase();
    return orders.filter(
      (o) =>
        o.id.toLowerCase().includes(q) ||
        (o.po_number ?? '').toLowerCase().includes(q) ||
        (o.vendor_name ?? '').toLowerCase().includes(q),
    );
  }, [orders, search]);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
      <div className="text-[11px] font-semibold text-amber-900">
        Match to an existing order
      </div>
      {error && (
        <div className="text-[11px] text-red-700 font-mono">{error}</div>
      )}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search open orders…"
        className="w-full text-[12px] px-2.5 py-1.5 rounded border border-amber-200 bg-white"
      />
      {filtered === null ? (
        <div className="text-[11px] text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic">
          No matching open orders.
        </div>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {filtered.slice(0, 20).map((o) => {
            const already = currentRefs.includes(o.id);
            return (
              <li key={o.id}>
                <button
                  type="button"
                  disabled={already}
                  onClick={() => onPick(o.id)}
                  className="w-full text-left px-2 py-1.5 rounded bg-white hover:bg-amber-100 active:bg-amber-200 disabled:bg-slate-100 disabled:cursor-not-allowed transition"
                >
                  <div className="text-[12px] font-medium text-slate-900 truncate">
                    {o.vendor_name ?? '(unknown vendor)'}{' '}
                    <span className="text-slate-400">· {o.status}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">
                    {o.po_number ? `${o.po_number} · ` : ''}
                    {o.id}
                  </div>
                  {already && (
                    <div className="text-[10px] text-emerald-700 mt-0.5">
                      ✓ Already referenced
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ReadOnlySummary({
  payload,
}: {
  payload: ParsedPayload | null;
}){
  if (!payload) {
    return (
      <div className="text-[11px] text-slate-400 italic">No payload.</div>
    );
  }
  const total = leafNumber(payload.total);
  const orderedAt = leafString(payload.ordered_at);
  const shipping = payload.shipping ?? {};
  return (
    <dl className="text-[12px] space-y-1">
      {total !== '' && (
        <SummaryRow label="Total" value={`$${total}`} />
      )}
      {orderedAt && <SummaryRow label="Ordered at" value={orderedAt} />}
      {Object.entries(shipping).length > 0 && (
        <div>
          <dt className="text-slate-500">Shipping</dt>
          <dd className="ml-2 mt-0.5 space-y-0.5">
            {Object.entries(shipping).map(([k, v]) => {
              const sv = leafString(v);
              if (!sv) return null;
              return (
                <div key={k} className="text-[11px] text-slate-600">
                  <span className="font-mono text-slate-400">{k}:</span> {sv}
                </div>
              );
            })}
          </dd>
        </div>
      )}
      {total === '' && !orderedAt && Object.keys(shipping).length === 0 && (
        <div className="text-[11px] text-slate-400 italic">
          No read-only fields parsed.
        </div>
      )}
    </dl>
  );
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: string;
}){
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 text-right truncate">{value}</dd>
    </div>
  );
}

function OpResultBanner({
  result,
  onDismiss,
}: {
  result:
    | { kind: 'success'; result: CommitResult }
    | { kind: 'error'; message: string };
  onDismiss: () => void;
}){
  if (result.kind === 'error') {
    return (
      <div className="bg-red-50 border-b border-red-200 px-3 py-2 flex items-start gap-2">
        <div className="flex-1 text-[12px] text-red-900">
          <div className="font-semibold">Operation failed</div>
          <div className="text-[11px] font-mono mt-0.5 break-words">
            {result.message}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="w-6 h-6 rounded hover:bg-red-100 flex items-center justify-center"
        >
          <Icon name="x" className="w-3.5 h-3.5 text-red-700" strokeWidth={2.5} />
        </button>
      </div>
    );
  }
  const r = result.result;
  const tone =
    r.result === 'dispatched'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
      : 'bg-green-50 border-green-200 text-green-900';
  return (
    <div className={`border-b px-3 py-2 flex items-start gap-2 ${tone}`}>
      <div className="flex-1 text-[12px]">
        <div className="font-semibold">Committed</div>
        <div className="text-[11px] mt-0.5">
          Matcher result: <span className="font-mono">{r.result}</span>
          {r.order_id && (
            <>
              {' · '}order <span className="font-mono">{r.order_id.slice(0, 8)}</span>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="w-6 h-6 rounded hover:bg-white/40 flex items-center justify-center"
      >
        <Icon name="x" className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
